import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import {
  useCreateOrUpdateMyFavouriteMutation,
  UserFavouriteType,
  type GetVendorQuery,
  GetVendorDocument,
} from '@/generated/graphql'
import useTenant from '@/hooks/useTenant'
import { useNotifications } from '@/store/notificationStore'
import { MenuVendor } from '../../domain/menu-vendor'
import { useAppStateStore } from '../../../common/store/appStateStore'
import { useVendorLocationData } from '../../store/vendorLocationStore'

export const useVendorHeader = (vendor: MenuVendor | null) => {
  const [modalOpen, setModalOpen] = useState(false)
  const { t } = useTranslation()
  const tenant = useTenant()
  const { showSuccess, showError } = useNotifications()
  const [updateMyFavourite] = useCreateOrUpdateMyFavouriteMutation()
  const vendorLocationData = useVendorLocationData()
  const { deliveryFeeExTax, minOrderValueExTax, minNoticePeriod } = vendorLocationData

  const { data: vendorData, loading: vendorLoading } = useQuery<GetVendorQuery>(GetVendorDocument, {
    variables: { permalink: vendor?.permalink || '', tenantId: tenant.id },
    fetchPolicy: 'network-only',
  })

  const toggleFavourite = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (!vendor) return

    const newFavoriteStatus = !vendorData?.vendor?.isMyFavourite

    updateMyFavourite({
      variables: {
        input: {
          favouriteId: Number.parseInt(vendor.id, 10),
          favouriteType: UserFavouriteType.Supplier,
          isFavourite: newFavoriteStatus,
        },
      },
      update: (cache) => {
        // Update the vendor cache to reflect the new favorite status
        const existingData = cache.readQuery<GetVendorQuery>({
          query: GetVendorDocument,
          variables: { permalink: vendor.permalink, tenantId: tenant.id },
        })

        if (existingData?.vendor) {
          cache.writeQuery<GetVendorQuery>({
            query: GetVendorDocument,
            variables: { permalink: vendor.permalink, tenantId: tenant.id },
            data: {
              ...existingData,
              vendor: {
                ...existingData.vendor,
                isMyFavourite: newFavoriteStatus,
              },
            },
          })
        }
      },
    })
      .then(() => {
        const message = newFavoriteStatus
          ? t('marketplace:addedToFavorites', 'Added to favourites')
          : t('marketplace:removedFromFavorites', 'Removed from favourites')
        showSuccess(message)
      })
      .catch((error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('marketplace:favoriteUpdateError', 'Failed to update favourite')
        showError(errorMessage)
      })
  }

  const openModal = () => setModalOpen(true)
  const closeModal = () => setModalOpen(false)

  return {
    // State
    modalOpen,
    vendorData,
    vendorLoading,

    // Actions
    toggleFavourite,
    openModal,
    closeModal,

    // Computed values
    isMyFavourite: vendorData?.vendor?.isMyFavourite,
    minNoticePeriod,
    deliveryFeeExTax,
    minOrderValueExTax,
  }
}
